import { useEffect, useState } from "react";
import { CheckCircle2, Download, ExternalLink, Pencil, X } from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";
import { LEGAL_DOCS } from "@/pages/Legal";

const LEGAL_FIELDS = [
  ["legal_entity_name", "Registered legal entity name"],
  ["legal_registered_address", "Registered address"],
  ["legal_cin", "CIN (if applicable)"],
  ["legal_gstin", "GSTIN (if applicable)"],
  ["legal_pan", "PAN (if applicable)"],
];

const AdminLegal = () => {
  const [entity, setEntity] = useState({});
  const [docs, setDocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    api.get("/admin/settings").then((r) => setEntity(r.data.legal || {})).catch(() => {});
    api.get("/admin/legal").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveEntity = async (e) => {
    e.preventDefault();
    setNotice("");
    setError("");
    try {
      await api.put("/admin/settings", entity);
      setNotice("Legal entity details saved — all documents re-render with them.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (d) => {
    setEdit(d);
    setF({ title: d.title, content: d.content, reviewed: d.reviewed });
  };

  const saveDoc = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.put(`/admin/legal/${edit.key}`, f);
      setEdit(null);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-legal">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Legal documents</h1>
        <p className="text-sm text-brand-mist">
          Four living documents rendered from this editor. Each stays publicly marked as pending review until you
          confirm a qualified legal professional has reviewed it.
        </p>
      </div>

      <form onSubmit={saveEntity} className="surface-card space-y-6 p-6 sm:p-8" data-testid="legal-entity-card">
        <div className="space-y-1">
          <h2 className="font-display text-lg font-semibold text-brand-snow">Contracting entity</h2>
          <p className="text-sm text-brand-mist">Filled once, inserted into every document. Empty fields render as visible "to be confirmed" markers — never invented.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {LEGAL_FIELDS.map(([k, label]) => (
            <Field key={k} label={label} htmlFor={`le-${k}`}>
              <TextInput id={`le-${k}`} testid={`legal-entity-${k}`} value={entity[k] || ""} onChange={(e) => setEntity({ ...entity, [k]: e.target.value })} />
            </Field>
          ))}
        </div>
        {notice && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="legal-entity-saved"><CheckCircle2 size={15} /> {notice}</p>}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button type="submit" data-testid="legal-entity-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Save entity details</button>
      </form>

      <div className="surface-card divide-y divide-brand-mist/10" data-testid="legal-docs-list">
        {docs.map((d) => {
          const meta = LEGAL_DOCS.find((l) => l.key === d.key);
          return (
            <div key={d.key} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4" data-testid={`legal-row-${d.key}`}>
              <div>
                <p className="text-sm font-medium text-brand-snow">{d.title}</p>
                <p className={`text-xs ${d.reviewed ? "text-brand-emerald" : "text-brand-azure"}`}>
                  {d.reviewed ? "Reviewed — published as final" : "Pending legal review — banner shown publicly"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {d.key === "non-disclosure-agreement" && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/non-disclosure-agreement.pdf`} data-testid="legal-nda-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
                    <Download size={12} /> PDF
                  </a>
                )}
                {meta && (
                  <a href={meta.path} target="_blank" rel="noreferrer" data-testid={`legal-view-${d.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-azure hover:text-brand-azure">
                    <ExternalLink size={12} /> View
                  </a>
                )}
                <button onClick={() => openEdit(d)} data-testid={`legal-edit-${d.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
                  <Pencil size={12} /> Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {edit && f && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setEdit(null)} data-testid="legal-editor-modal">
          <form onSubmit={saveDoc} className="h-full w-full max-w-3xl space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:max-h-[92vh] sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-bold text-brand-snow">Edit: {edit.title}</h2>
                <p className="mt-1 text-xs text-brand-mist">Entity tokens available: {"{{entity_name}}, {{entity_address}}, {{cin}}, {{gstin}}, {{pan}}"}</p>
              </div>
              <button type="button" onClick={() => setEdit(null)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <Field label="Document title" htmlFor="ld-title"><TextInput id="ld-title" testid="ld-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
            <Field label="Content" htmlFor="ld-content" hint="'## ' for section headings, '- ' for bullets, blank line between paragraphs.">
              <TextArea id="ld-content" rows={18} testid="ld-content" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} />
            </Field>
            <label className="flex items-start gap-3 text-sm text-brand-mist" data-testid="ld-reviewed-label">
              <input type="checkbox" checked={f.reviewed} onChange={(e) => setF({ ...f, reviewed: e.target.checked })} data-testid="ld-reviewed" className="mt-0.5 h-4 w-4 accent-brand-emerald" />
              A qualified legal professional has reviewed this document — remove the public "pending review" banner.
            </label>
            <button type="submit" data-testid="legal-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Save document</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminLegal;
